import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Card, Failed, Loading, Tile } from '@/components/bits'
import { orgName, useOrg } from '@/org/OrgProvider'
import { useAsync } from '@/data/useAsync'
import {
  cancelSickness,
  confirmPay,
  confirmReturn,
  loadSicknessBoard,
  markCertificationReceived,
} from '@/data/sickness'
import type { EpisodeRow, OutstandingCert, PayTreatment } from '@/data/sickness'
import { shortDate } from '@/lib/format'

/**
 * The sickness board. What HR and heads of department actually work from.
 *
 * Ordered by what gets forgotten, not by what is interesting. Somebody off
 * today is visible to everybody and needs no prompting; a return to work
 * interview nobody held is invisible, because the person is back at work and
 * nothing reminds anyone. So that list is second, not last.
 *
 * Row level security decides what appears: HR sees the hotel, a head of
 * department sees their own departments, front desk cannot reach this screen's
 * data at all. Cancelled absences are filtered everywhere, because a cancelled
 * one is a record of a mistake rather than of somebody being ill.
 */

function today(): string {
  return new Date().toISOString().slice(0, 10)
}

function dayCount(e: EpisodeRow): number {
  const from = new Date(`${e.firstAbsentDate}T00:00:00Z`).getTime()
  const to = new Date(`${e.lastAbsentDate ?? today()}T00:00:00Z`).getTime()
  return Math.max(1, Math.round((to - from) / 86_400_000) + 1)
}

const TREATMENTS: { value: PayTreatment; label: string }[] = [
  { value: 'ssp', label: 'SSP only' },
  { value: 'company_paid', label: 'Company sick pay' },
  { value: 'company_unpaid', label: 'Unpaid, company scheme' },
  { value: 'unpaid_other', label: 'Unpaid, other reason' },
]

function CertRow({ c, onDone }: { c: OutstandingCert; onDone: () => void }) {
  const [busy, setBusy] = useState(false)
  const overdue = c.dueOn !== null && c.dueOn < today()

  return (
    <tr>
      <td>{c.teamMember}</td>
      <td>{c.kind === 'fit_note' ? 'Fit note' : 'Self-certification'}</td>
      <td>{c.dueOn === null ? '—' : shortDate(c.dueOn)}</td>
      <td>{overdue ? <span className="pill sev-overdue">Overdue</span> : 'Awaited'}</td>
      <td>
        <button
          type="button"
          className="btn quiet sm"
          disabled={busy}
          onClick={() => {
            setBusy(true)
            markCertificationReceived(c.id, today())
              .then(onDone)
              .finally(() => setBusy(false))
          }}
        >
          {busy ? 'Saving…' : 'Received'}
        </button>
      </td>
    </tr>
  )
}

function EpisodeActions({ e, onChanged }: { e: EpisodeRow; onChanged: () => void }) {
  const [mode, setMode] = useState<'idle' | 'return' | 'cancel'>('idle')
  const [returnDate, setReturnDate] = useState(today())
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function run(fn: () => Promise<unknown>) {
    setBusy(true)
    setError(null)
    try {
      await fn()
      setMode('idle')
      onChanged()
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause))
    } finally {
      setBusy(false)
    }
  }

  if (mode === 'return') {
    return (
      <div className="inlineform">
        <label className="stackfield">
          <span>First day back at work</span>
          <input
            type="date"
            value={returnDate}
            min={e.firstAbsentDate}
            onChange={(ev) => setReturnDate(ev.target.value)}
          />
        </label>
        <p className="note">
          The day they actually returned, not their last sick day. Any days recorded from this date
          that had not happened yet are removed, and any shift already marked SSP is put back.
        </p>
        <div className="actions">
          <button
            className="btn primary sm"
            type="button"
            disabled={busy}
            onClick={() => void run(() => confirmReturn(e.episodeId, returnDate))}
          >
            {busy ? 'Saving…' : 'Close the absence'}
          </button>
          <button className="btn quiet sm" type="button" onClick={() => setMode('idle')}>
            Cancel
          </button>
        </div>
        {error !== null && <p className="note sev-critical">{error}</p>}
      </div>
    )
  }

  if (mode === 'cancel') {
    return (
      <div className="inlineform">
        <label className="stackfield">
          <span>Why is this being cancelled?</span>
          <input
            type="text"
            value={reason}
            placeholder="e.g. logged against the wrong person"
            onChange={(ev) => setReason(ev.target.value)}
          />
        </label>
        <p className="note">
          For an absence that should never have been recorded. The record is kept and marked
          cancelled rather than deleted, so what happened stays traceable, and it disappears from
          every list here.
        </p>
        <div className="actions">
          <button
            className="btn primary sm"
            type="button"
            disabled={busy || reason.trim() === ''}
            onClick={() => void run(() => cancelSickness(e.episodeId, reason))}
          >
            {busy ? 'Saving…' : 'Cancel this absence'}
          </button>
          <button className="btn quiet sm" type="button" onClick={() => setMode('idle')}>
            Keep it
          </button>
        </div>
        {error !== null && <p className="note sev-critical">{error}</p>}
      </div>
    )
  }

  return (
    <div className="actions">
      <button className="btn quiet sm" type="button" onClick={() => setMode('return')}>
        They are back
      </button>
      <button className="btn quiet sm" type="button" onClick={() => setMode('cancel')}>
        Logged in error
      </button>
    </div>
  )
}

function PayRow({
  episodeId,
  teamMember,
  days,
  onChanged,
}: {
  episodeId: string
  teamMember: string
  days: number
  onChanged: () => void
}) {
  const [treatment, setTreatment] = useState<PayTreatment>('ssp')
  const [busy, setBusy] = useState(false)

  return (
    <tr>
      <td>{teamMember}</td>
      <td className="mono">{days}</td>
      <td>
        <select
          value={treatment}
          onChange={(e) => setTreatment(e.target.value as PayTreatment)}
          aria-label={`Pay treatment for ${teamMember}`}
        >
          {TREATMENTS.map((t) => (
            <option key={t.value} value={t.value}>
              {t.label}
            </option>
          ))}
        </select>
      </td>
      <td>
        <button
          type="button"
          className="btn quiet sm"
          disabled={busy}
          onClick={() => {
            setBusy(true)
            confirmPay(episodeId, treatment, null)
              .then(onChanged)
              .finally(() => setBusy(false))
          }}
        >
          {busy ? 'Saving…' : 'Confirm'}
        </button>
      </td>
    </tr>
  )
}

export function Sickness() {
  const { current } = useOrg()
  const board = useAsync((_s) => loadSicknessBoard(current.id), [current.id])

  if (board.state === 'loading') return <Loading what="the sickness board" />
  if (board.state === 'error') return <Failed message={board.message} onRetry={board.reload} />

  const b = board.data
  const overdueCerts = b.certifications.filter((c) => c.dueOn !== null && c.dueOn < today()).length

  return (
    <section className="screen">
      <h1>Sickness</h1>
      {/* The board is scoped to one hotel by row level security, so an absence
          logged at the other property is correctly invisible here. Without the
          name on the page that reads as a missing record rather than a
          different hotel. */}
      <p className="eyebrow">{orgName(current)}</p>

      <div className="tiles">
        <Tile label="Off today" value={b.offToday} />
        <Tile label="Open absences" value={b.open.length} />
        <Tile label="Returns without an interview" value={b.awaitingInterview.length} />
        <Tile label="Paperwork outstanding" value={b.certifications.length}>
          {overdueCerts > 0 && <div className="note">{overdueCerts} past its due date</div>}
        </Tile>
        <Tile label="Pay not yet reviewed" value={b.unreviewedPay.length} />
        <Tile label="Waiting to reach Planday" value={b.rotaWaiting.length} />
      </div>

      {b.rotaWaiting.length > 0 && (
        <Card title="Waiting to reach Planday">
          {/* This card exists because nothing else said so. The screens record
              a decision and a separate worker performs the Planday write, so
              with the worker stopped the rota silently never changes and every
              other panel here still looks completely correct. */}
          <p className="callout">
            <strong>
              {b.rotaWaiting.length} day{b.rotaWaiting.length === 1 ? '' : 's'} not yet applied to
              the rota.
            </strong>{' '}
            Recording an absence here does not change Planday by itself: a background worker does
            that. If this number does not fall within a few minutes, the worker is not running and
            somebody needs to start it.
          </p>
          <table className="grid">
            <thead>
              <tr>
                <th>Day</th>
                <th>What is queued</th>
                <th>Last error</th>
              </tr>
            </thead>
            <tbody>
              {b.rotaWaiting.map((w) => (
                <tr key={`${w.absenceDate}-${w.status}-${w.error ?? ''}`}>
                  <td>{shortDate(w.absenceDate)}</td>
                  <td>
                    {w.revertRequested
                      ? 'Put the shift type back'
                      : w.status === 'failed'
                        ? 'Failed, needs a look'
                        : 'Set the shift to the sick pay type'}
                  </td>
                  <td className="note">{w.error ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      <Card title="Currently off">
        {b.open.length === 0 ? (
          <p className="note">Nobody is recorded as off sick.</p>
        ) : (
          <table className="grid">
            <thead>
              <tr>
                <th>Team member</th>
                <th>Department</th>
                <th>Since</th>
                <th>Days</th>
                <th>Expected back</th>
                <th>Last 6 months</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {b.open.map((e) => (
                <tr key={e.episodeId}>
                  <td>
                    {e.teamMember}
                    {e.jobTitle !== null && <div className="note">{e.jobTitle}</div>}
                  </td>
                  <td>{e.department ?? '—'}</td>
                  <td>{shortDate(e.firstAbsentDate)}</td>
                  <td className="mono">{dayCount(e)}</td>
                  <td>
                    {e.expectedReturnDate === null ? (
                      <span className="note">Not said</span>
                    ) : (
                      shortDate(e.expectedReturnDate)
                    )}
                  </td>
                  <td className="mono">
                    {e.daysAbsentLast6Months}
                    <span className="note"> in {e.absencesLast6Months}</span>
                  </td>
                  <td>
                    <EpisodeActions e={e} onChanged={board.reload} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <Card title="Back at work, no return to work interview held">
        <p className="note">
          The list that gets forgotten. The person is back, so nothing prompts anybody, and the
          interview is the part of the procedure that is actually meant to catch a pattern.
        </p>
        {b.awaitingInterview.length === 0 ? (
          <p className="note">Nothing outstanding.</p>
        ) : (
          <table className="grid">
            <thead>
              <tr>
                <th>Team member</th>
                <th>Department</th>
                <th>Absence</th>
                <th>Working days</th>
                <th>Last 6 months</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {b.awaitingInterview.map((e) => (
                <tr key={e.episodeId}>
                  <td>{e.teamMember}</td>
                  <td>{e.department ?? '—'}</td>
                  <td>
                    {shortDate(e.firstAbsentDate)}
                    {e.lastAbsentDate !== null && <> to {shortDate(e.lastAbsentDate)}</>}
                  </td>
                  <td className="mono">
                    {e.workingDaysAbsent}
                    {e.daysNotYetClassified > 0 && (
                      <span className="note"> · {e.daysNotYetClassified} not yet from the rota</span>
                    )}
                  </td>
                  <td className="mono">{e.daysAbsentLast6Months}</td>
                  <td>
                    <Link className="btn quiet sm" to={`/absence/${e.episodeId}`}>
                      Hold the interview
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <Card title="Paperwork outstanding">
        <p className="note">
          Self-certification covers the first 7 calendar days. A fit note is required from the 8th.
        </p>
        {b.certifications.length === 0 ? (
          <p className="note">Nothing awaited.</p>
        ) : (
          <table className="grid">
            <thead>
              <tr>
                <th>Team member</th>
                <th>Document</th>
                <th>Due</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {b.certifications.map((c) => (
                <CertRow key={c.id} c={c} onDone={board.reload} />
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <Card title="Pay not yet reviewed">
        <p className="note">
          Every sick day is recorded as SSP because reception cannot know whether a company scheme
          applies, and the statutory minimum can never pay somebody below their entitlement.
          Confirming is how a default becomes a decision. SSP has been payable from day one since 6
          April 2026.
        </p>
        {b.unreviewedPay.length === 0 ? (
          <p className="note">Nothing waiting.</p>
        ) : (
          <table className="grid">
            <thead>
              <tr>
                <th>Team member</th>
                <th>Days</th>
                <th>Treatment</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {b.unreviewedPay.map((p) => (
                <PayRow
                  key={p.episodeId}
                  episodeId={p.episodeId}
                  teamMember={p.teamMember}
                  days={p.days}
                  onChanged={board.reload}
                />
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </section>
  )
}
